import React from 'react';
import {Badge, Button, Card, CardBody, CardHeader, FormGroup, Input, Label} from 'reactstrap';
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import ObjectOverride from "../ObjectOverride";
import {withRouter} from "react-router-dom";
import Framework from "@beardeddevops/react.framework";
import MrpLocationControl from "components/Parts/MrpLocationControl";
import {applyMrpLocationToRequest, getMrpActiveLocation, getMrpLocationSignature, syncMrpActiveLocation} from "utils/mrpLocation";

const LOCATION_NOTE = 'ERP reads and writes follow the active location shown here. Changing it updates data across ERP screens.';

const SCRAP_REASON_OPTIONS = [
	{value: 'damaged', label: 'Damaged'},
	{value: 'out_of_spec', label: 'Out of Spec'},
	{value: 'machining_error', label: 'Machining Error'},
	{value: 'setup_error', label: 'Setup Error'},
	{value: 'material_defect', label: 'Material Defect'},
	{value: 'other', label: 'Other'},
];

const ACTION_BUTTON_BASE = {
	height: '46px',
	borderRadius: '10px',
	fontWeight: 700,
	lineHeight: 1,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '0 16px',
};

const ACTION_BUTTON_SMALL = {
	height: '38px',
	borderRadius: '10px',
	fontWeight: 700,
	lineHeight: 1,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '0 12px',
};

class MrpStationWork extends ObjectOverride {
	constructor(props) {
		super(props, Types.Mrp_Jobs._name);
		this.state = {
			loading: true,
			loadError: '',
			jobs: [],
			manufacturers: [],
			calibers: [],
			productTypes: [],
			selectedStationKey: '',
			selectedJobId: '',
			jobViewMode: 'available',
			softCompletedJobs: {},
			employeeName: this.getInitialEmployeeName(),
			activePartByStation: {},
			scrapReason: SCRAP_REASON_OPTIONS[0].value,
			scrapQuantity: '1',
			scrapNote: '',
			scrapPartKey: null,
			manualType: 'labor',
			manualMinutes: '',
			manualNote: '',
			editingEntryId: null,
			editMinutes: '',
			editSeconds: '',
			editNote: '',
			serialInputByStation: {},
			serialGateProductDetailsByStation: {},
			now: Date.now(),
		};
		this._tickInterval = null;
	}

	componentDidMount() {
		this.ensureEmployeeName();
		syncMrpActiveLocation(this.props.rootState);
		this.loadJobs().then();
		this.loadProductDetailLookups().then();
	}

	componentDidUpdate(prevProps, prevState) {
		if (getMrpLocationSignature(prevProps?.rootState) !== getMrpLocationSignature(this.props?.rootState)) {
			this.setState({
				selectedStationKey: '',
				selectedJobId: '',
				softCompletedJobs: {},
				activePartByStation: {},
				serialInputByStation: {},
				serialGateProductDetailsByStation: {},
			}, () => {
				this.loadJobs().then();
			});
			return;
		}
		if (prevProps?.rootState?.name !== this.props?.rootState?.name) {
			this.ensureEmployeeName();
		}
		if (
			prevState.selectedStationKey !== this.state.selectedStationKey
			|| prevState.selectedJobId !== this.state.selectedJobId
			|| prevState.jobs !== this.state.jobs
		) {
			this.ensureActivePartSelection();
			this.manageTick();
		}
	}

	componentWillUnmount() {
		this.stopTick();
	}

	getStoredEmployeeName = () => {
		try {
			return localStorage.getItem(this.getEmployeeStorageKey()) || '';
		} catch (error) {
			return '';
		}
	}

	getEmployeeStorageKey = () => {
		const account = `${this.props?.rootState?.account_number || 'account'}`;
		const user = `${this.props?.rootState?.user || 'account-owner'}`;
		return `mrp_employee_name:${account}:${user}`;
	}

	getCurrentUserName = () => {
		const rawName = this.props?.rootState?.name;
		return typeof rawName === 'string' ? rawName.trim() : '';
	}

	getInitialEmployeeName = () => {
		const stored = this.getStoredEmployeeName().trim();
		if (stored) {
			return stored;
		}
		return this.getCurrentUserName();
	}

	ensureEmployeeName = () => {
		const currentValue = `${this.state.employeeName || ''}`.trim();
		if (currentValue) {
			return;
		}
		const userName = this.getCurrentUserName();
		if (!userName) {
			return;
		}
		this.setState({employeeName: userName});
		this.setStoredEmployeeName(userName);
	}

	setStoredEmployeeName = (value) => {
		try {
			localStorage.setItem(this.getEmployeeStorageKey(), value);
		} catch (error) {
			// ignore storage errors
		}
	}

	getAccountParent = () => {
		return this.props?.rootState?.account_number || '9999-9999999';
	}

	getLocationContext = () => {
		return getMrpActiveLocation(this.props?.rootState);
	}

	formatNamedLookupLabel = (entry) => `${entry?.name || entry?.label || entry?.title || ''}`.trim()

	mapLookupItemsToOptions = (items) => (Array.isArray(items) ? items : [])
		.map((item) => {
			const id = `${item?.pkey || item?.id || ''}`.trim();
			const label = this.formatNamedLookupLabel(item);
			if (!id || !label) {
				return null;
			}
			return {id, label};
		})
		.filter(Boolean)

	normalizeLookupSelectedValue = (value) => {
		const normalizedValue = `${value || ''}`.trim();
		if (/^\d+$/.test(normalizedValue)) {
			return normalizedValue;
		}
		if (/^\d+-\d+$/.test(normalizedValue)) {
			return `${normalizedValue.split('-').pop() || ''}`.trim();
		}
		return '';
	}

	getEmptySerialGateProductDetails = () => ({
		manufacturer: '',
		manufacturer_other: '',
		caliber: '',
		caliber_other: '',
		type: '',
		upc: '',
	})

	parseWorkflow = (workflow) => {
		if (!workflow) {
			return null;
		}
		if (typeof workflow === 'string') {
			try {
				return JSON.parse(workflow);
			} catch (error) {
				return null;
			}
		}
		if (typeof workflow === 'object') {
			return workflow;
		}
		return null;
	}

	normalizeJob = (job) => {
		const workflow = this.parseWorkflow(job.workflow) || {};
		const id = job.pkey || job.id;
		const status = job.status || workflow?.job?.status || 'in_progress';
		const name = job.name || workflow?.job?.name || `Job ${id}`;
		const modelName = workflow?.model?.name || workflow?.job?.model_name || '';
		const quantity = workflow?.job?.quantity ?? workflow?.quantity ?? job.quantity ?? job.job_quantity ?? job.qty ?? '';
		const lotId = job.lot || workflow?.job?.lot_id || '';
		return {
			...job,
			id,
			name,
			status,
			workflow,
			modelName,
			quantity,
			lotId,
		};
	}

	loadProductDetailLookups = async () => {
		try {
			await Promise.all([
				this.loadManufacturers(),
				this.loadCalibers(),
				this.loadProductTypes(),
			]);
		} catch (error) {
			// Keep ERP Work usable even if lookup helpers fail; manual entry remains available.
		}
	}

	loadManufacturers = async () => {
		let manufacturerModel = new API.BuildClass(Types.Manufacturers._name, API.RequestType.READ_ALL);
		manufacturerModel.parent = this.getAccountParent();
		manufacturerModel.limit = 1000;
		const result = await manufacturerModel.submit(null, API.RequestType.READ_ALL);
		const manufacturers = this.mapLookupItemsToOptions(result?.items);
		this.setState({manufacturers});
	}

	loadCalibers = async () => {
		let caliberModel = new API.BuildClass(Types.Calibers._name, API.RequestType.READ_ALL);
		caliberModel.parent = this.getAccountParent();
		caliberModel.limit = 1000;
		const result = await caliberModel.submit(null, API.RequestType.READ_ALL);
		const calibers = this.mapLookupItemsToOptions(result?.items);
		this.setState({calibers});
	}

	loadProductTypes = async () => {
		let typeModel = new API.BuildClass(Types.Products_Types._name, API.RequestType.READ_ALL);
		typeModel.limit = 200;
		const result = await typeModel.submit(null, API.RequestType.READ_ALL);
		const productTypes = (Array.isArray(result?.items) ? result.items : [])
			.map((item) => {
				const value = this.formatNamedLookupLabel(item);
				if (!value) {
					return null;
				}
				return {value, label: value};
			})
			.filter(Boolean);
		this.setState({productTypes});
	}

	hasSerialGateProductDetails = (details) => {
		if (!details || typeof details !== 'object') {
			return false;
		}
		return Boolean(
			`${details.manufacturer || ''}`.trim()
			|| `${details.manufacturer_other || ''}`.trim()
			|| `${details.caliber || ''}`.trim()
			|| `${details.caliber_other || ''}`.trim()
			|| `${details.type || ''}`.trim()
			|| `${details.upc || ''}`.trim()
		);
	}

	buildHydratedSerialGateProductState = (jobs, previousStateMap = {}) => {
		const hydratedState = {...previousStateMap};
		jobs.forEach((job) => {
			const stations = Array.isArray(job?.workflow?.stations) ? job.workflow.stations : [];
			stations.forEach((station) => {
				const persistedDetails = this.getWorkflowSerialGateProductDetails(job, station);
				if (!this.hasSerialGateProductDetails(persistedDetails)) {
					return;
				}
				const stateKey = this.getSerialInputKey(job.id, station);
				const previousDetails = previousStateMap[stateKey];
				hydratedState[stateKey] = this.hasSerialGateProductDetails(previousDetails)
					? {
						...persistedDetails,
						...previousDetails
					}
					: persistedDetails;
			});
		});
		return hydratedState;
	}

	loadJobs = async () => {
		this.setState({loading: true, loadError: ''});
		try {
			let jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'GET_NORMALIZED');
			jobModel.parent = this.getAccountParent();
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			const result = await jobModel.submit(null, 'GET_NORMALIZED');
			const rawItems = result?.items ?? result?.item ?? result?.data ?? result ?? [];
			let items = Array.isArray(rawItems) ? rawItems : (rawItems ? [rawItems] : []);
			if (items.length === 0 && rawItems && typeof rawItems === 'object') {
				const hasIdentity = rawItems.pkey || rawItems.id;
				items = hasIdentity ? [rawItems] : [];
			}
			const jobs = items.map(this.normalizeJob);
			const stationOptions = this.getStationOptions(jobs);
			const selectedStationKey = this.state.selectedStationKey || stationOptions[0]?.key || '';
			const selectedStationJobs = this.getAllJobsForStation(jobs, selectedStationKey);
			let softCompletedJobs = this.state.softCompletedJobs || {};
			const selectedJobInStation = selectedStationJobs.find((job) => job.id === this.state.selectedJobId) || null;
			if (selectedStationKey && selectedJobInStation && this.isJobCompleteForStation(selectedJobInStation, selectedStationKey)) {
				softCompletedJobs = {
					...softCompletedJobs,
					[selectedStationKey]: {
						...(softCompletedJobs[selectedStationKey] || {}),
						[selectedJobInStation.id]: true
					}
				};
			}
			const jobBuckets = this.getJobBucketsForStation(jobs, selectedStationKey, softCompletedJobs);
			const jobsForMode = this.state.jobViewMode === 'completed' ? jobBuckets.completed : jobBuckets.available;
			const selectedJobId = this.state.selectedJobId && selectedStationJobs.some((job) => job.id === this.state.selectedJobId)
				? this.state.selectedJobId
				: (jobsForMode[0]?.id || selectedStationJobs[0]?.id || '');
			const serialGateProductDetailsByStation = this.buildHydratedSerialGateProductState(
				jobs,
				this.state.serialGateProductDetailsByStation || {}
			);
			this.setState({
				jobs,
				selectedStationKey,
				selectedJobId,
				softCompletedJobs,
				serialGateProductDetailsByStation,
				loading: false,
			});
		} catch (error) {
			this.setState({loading: false, loadError: 'Failed to load jobs.'});
		}
	}

	getStationKey = (station) => {
		if (!station) {
			return '';
		}
		if (station.station_id || station.station_id === 0) {
			return `id:${station.station_id}`;
		}
		const name = station.station_name || '';
		return `name:${name}`.toLowerCase();
	}

	getStationOptions = (jobs) => {
		const options = [];
		const seen = new Set();
		jobs.forEach((job) => {
			const stations = job.workflow?.stations || [];
			stations.forEach((station) => {
				const key = this.getStationKey(station);
				if (!key || seen.has(key)) {
					return;
				}
				seen.add(key);
				options.push({
					key,
					label: station.station_name || `Station ${station.station_id}`,
				});
			});
		});
		return options;
	}

	isJobCompleteForStation = (job, stationKey) => {
		const station = this.getStationForJob(job, stationKey);
		if (!station) {
			return false;
		}
		return this.isStatusComplete(station.status) || this.isStatusComplete(job.status);
	}

	getAllJobsForStation = (jobs, stationKey) => {
		if (!stationKey) {
			return [];
		}
		return jobs.filter((job) => Boolean(this.getStationForJob(job, stationKey)));
	}

	getJobBucketsForStation = (jobs, stationKey, softCompletedJobsOverride = null) => {
		const buckets = {available: [], completed: []};
		if (!stationKey) {
			return buckets;
		}
		const softCompletedJobs = softCompletedJobsOverride || this.state.softCompletedJobs || {};
		const flaggedByStation = softCompletedJobs[stationKey] || {};
		jobs.forEach((job) => {
			if (!this.getStationForJob(job, stationKey)) {
				return;
			}
			const isCompleted = this.isJobCompleteForStation(job, stationKey);
			if (isCompleted) {
				buckets.completed.push(job);
				if (flaggedByStation[job.id]) {
					buckets.available.push(job);
				}
				return;
			}
			buckets.available.push(job);
		});
		return buckets;
	}

	getJobsForStation = (jobs, stationKey) => {
		return this.getJobBucketsForStation(jobs, stationKey).available;
	}

	getCompletedJobsForStation = (jobs, stationKey) => {
		return this.getJobBucketsForStation(jobs, stationKey).completed;
	}

	getStationForJob = (job, stationKey) => {
		if (!job?.workflow?.stations) {
			return null;
		}
		return (job.workflow.stations || []).find((station) => this.getStationKey(station) === stationKey) || null;
	}

	getDefaultActivePartId = (station) => {
		const parts = station?.parts || [];
		const firstPart = parts[0];
		return firstPart?.job_station_part_id ?? null;
	}

	getActivePartId = (station) => {
		if (!station?.job_station_id) {
			return null;
		}
		const activeMap = this.state.activePartByStation || {};
		return activeMap[station.job_station_id] ?? null;
	}

	getSerialInputKey = (jobId, station) => {
		return `${jobId}:${station?.job_station_id || this.getStationKey(station) || 'station'}`;
	}

	getSerialInputValue = (jobId, station) => {
		const key = this.getSerialInputKey(jobId, station);
		return this.state.serialInputByStation[key] || '';
	}

	setSerialInputValue = (jobId, station, value) => {
		const key = this.getSerialInputKey(jobId, station);
		this.setState((prevState) => ({
			serialInputByStation: {
				...prevState.serialInputByStation,
				[key]: value
			}
		}));
	}

	clearSerialInputValue = (jobId, station) => {
		const key = this.getSerialInputKey(jobId, station);
		this.setState((prevState) => {
			const serialInputByStation = {...prevState.serialInputByStation};
			delete serialInputByStation[key];
			return {serialInputByStation};
		});
	}

	getSerialGatePersistenceKey = (station) => {
		return `${station?.station_id || station?.job_station_id || this.getStationKey(station) || 'station'}`;
	}

	getWorkflowSerialGateProductDetails = (job, station) => {
		const key = this.getSerialGatePersistenceKey(station);
		const productDetailsByStation = job?.workflow?.serial_tracking?.product_details_by_station;
		const persistedDetails = productDetailsByStation && typeof productDetailsByStation === 'object'
			? productDetailsByStation[key]
			: null;
		return {
			...this.getEmptySerialGateProductDetails(),
			...(persistedDetails && typeof persistedDetails === 'object' ? persistedDetails : {})
		};
	}

	getSerialGateProductDetails = (jobId, station, job = null) => {
		const key = this.getSerialInputKey(jobId, station);
		return {
			...this.getWorkflowSerialGateProductDetails(job, station),
			...(this.state.serialGateProductDetailsByStation[key] || {})
		};
	}

	setSerialGateProductField = (jobId, station, field, value) => {
		const key = this.getSerialInputKey(jobId, station);
		this.setState((prevState) => ({
			serialGateProductDetailsByStation: {
				...prevState.serialGateProductDetailsByStation,
				[key]: {
					...this.getEmptySerialGateProductDetails(),
					...(prevState.serialGateProductDetailsByStation[key] || {}),
					[field]: value
				}
			}
		}));
	}

	handleSerialGateLookupChange = (jobId, station, field) => (event) => {
		const value = this.normalizeLookupSelectedValue(event?.target?.value);
		const otherField = `${field}_other`;
		const key = this.getSerialInputKey(jobId, station);
		this.setState((prevState) => ({
			serialGateProductDetailsByStation: {
				...prevState.serialGateProductDetailsByStation,
				[key]: {
					...this.getEmptySerialGateProductDetails(),
					...(prevState.serialGateProductDetailsByStation[key] || {}),
					[field]: value,
					[otherField]: value ? '' : (prevState.serialGateProductDetailsByStation[key]?.[otherField] || '')
				}
			}
		}));
	}

	clearSerialGateProductDetails = (jobId, station) => {
		const key = this.getSerialInputKey(jobId, station);
		this.setState((prevState) => {
			const serialGateProductDetailsByStation = {...prevState.serialGateProductDetailsByStation};
			delete serialGateProductDetailsByStation[key];
			return {serialGateProductDetailsByStation};
		});
	}

	buildPendingSerialGateProductDetails = (jobId, station, job = null) => {
		const details = this.getSerialGateProductDetails(jobId, station, job);
		const manufacturer = this.normalizeLookupSelectedValue(details.manufacturer);
		const caliber = this.normalizeLookupSelectedValue(details.caliber);
		return {
			manufacturer,
			manufacturer_other: manufacturer ? '' : `${details.manufacturer_other || ''}`.trim(),
			caliber,
			caliber_other: caliber ? '' : `${details.caliber_other || ''}`.trim(),
			type: `${details.type || ''}`.trim(),
			upc: `${details.upc || ''}`.trim()
		};
	}

	getSerialGateProductDetailsError = (details) => {
		if (!details.manufacturer && !details.manufacturer_other) {
			return 'Choose or enter a manufacturer before completing the next unit.';
		}
		if (!details.caliber && !details.caliber_other) {
			return 'Choose or enter a caliber before completing the next unit.';
		}
		if (!details.type) {
			return 'Choose a type before completing the next unit.';
		}
		if (!details.upc) {
			return 'Enter a UPC before completing the next unit.';
		}
		return '';
	}

	ensureActivePartSelection = () => {
		const {jobs, selectedStationKey, selectedJobId, activePartByStation} = this.state;
		if (!selectedStationKey || !selectedJobId) {
			return;
		}
		const selectedJob = jobs.find((job) => job.id === selectedJobId) || null;
		const selectedStation = selectedJob ? this.getStationForJob(selectedJob, selectedStationKey) : null;
		if (!selectedStation?.job_station_id) {
			return;
		}
		if (activePartByStation[selectedStation.job_station_id]) {
			return;
		}
		const defaultPartId = this.getDefaultActivePartId(selectedStation);
		if (!defaultPartId) {
			return;
		}
		this.setState({
			activePartByStation: {
				...activePartByStation,
				[selectedStation.job_station_id]: defaultPartId
			}
		});
	}

	isStatusComplete = (status) => {
		if (!status) {
			return false;
		}
		const normalized = `${status}`.toLowerCase();
		return ['complete', 'completed', 'done', 'closed'].includes(normalized);
	}

	getNoPartsCompletedUnits = (station, jobQuantity = 1) => {
		const normalizedQuantity = Math.max(1, Number(jobQuantity) || 1);
		const rawUnits = Number(station?.no_parts_completed_units);
		if (Number.isFinite(rawUnits) && rawUnits > 0) {
			return Math.max(0, Math.min(normalizedQuantity, rawUnits));
		}
		if (station?.manual_complete || station?.status === 'completed') {
			return normalizedQuantity;
		}
		return 0;
	}

	getCompletedUnitsForStation = (station) => {
		const parts = station?.parts || [];
		let minUnits = null;
		parts.forEach((part) => {
			const requiredPerUnit = Number(part.required_quantity) || 0;
			if (requiredPerUnit <= 0) {
				return;
			}
			const completed = Number(part.completed_quantity) || 0;
			const unitsForPart = Math.floor(completed / requiredPerUnit);
			minUnits = minUnits === null ? unitsForPart : Math.min(minUnits, unitsForPart);
		});
		return minUnits === null ? 0 : minUnits;
	}

	getStationCompletedUnits = (station, jobQuantity = 1) => {
		const hasParts = (station?.parts || []).length > 0;
		return hasParts
			? this.getCompletedUnitsForStation(station)
			: this.getNoPartsCompletedUnits(station, jobQuantity);
	}

	getWorkflowAutoTransactionType = (job = null) => {
		const acquisitionEnabled = !!job?.workflow?.model?.acquisition?.enabled;
		const transferEnabled = !!job?.workflow?.model?.transfer?.enabled;
		if (acquisitionEnabled && transferEnabled) {
			return 'multiple';
		}
		if (transferEnabled) {
			return 'transfer';
		}
		if (acquisitionEnabled) {
			return 'acquisition';
		}
		return '';
	}

	getWorkflowCompletedUnitCount = (workflow, transactionType = '', station = null) => {
		if (!transactionType || transactionType === 'multiple') {
			return 0;
		}
		const completedUnits = workflow?.[transactionType]?.completed_units || [];
		if (!station) {
			return completedUnits.length;
		}
		const stationId = `${station?.station_id || ''}`;
		return completedUnits.filter((entry) => `${entry?.station_id || ''}` === stationId).length;
	}

	getWorkflowAutoTransactionDealerName = (job = null) => {
		const transactionType = this.getWorkflowAutoTransactionType(job);
		if (!transactionType || transactionType === 'multiple') {
			return '';
		}
		return job?.workflow?.model?.[transactionType]?.dealer_name || '';
	}

	isSerialGateRequired = (job, station) => {
		const requiredStationId = `${job?.workflow?.job?.serial_required_station_id || job?.workflow?.serial_tracking?.required_station_id || ''}`;
		const transactionType = this.getWorkflowAutoTransactionType(job);
		if (!transactionType || transactionType === 'multiple' || !station || !requiredStationId) {
			return false;
		}
		return `${station?.station_id || ''}` === requiredStationId;
	}

	applyPendingSerialCompletion = (workflow, station, serialNumber, targetUnitNumber, now, transactionType = '', productDetails = null) => {
		workflow.serial_tracking = workflow.serial_tracking || {};
		workflow.serial_tracking.required_station_id = workflow.job?.serial_required_station_id || workflow.serial_tracking.required_station_id || '';
		workflow.serial_tracking.pending_completion = {
			station_id: station?.station_id || null,
			station_name: station?.station_name || '',
			serial_number: serialNumber,
			target_unit_number: targetUnitNumber,
			auto_transaction_type: transactionType || workflow?.job?.auto_transaction_type || '',
			product_details: productDetails || {},
			requested_at: now,
		};
	}

	formatDuration = (minutesValue) => {
		const minutes = Math.max(0, Math.round(minutesValue || 0));
		if (minutes === 0) {
			return '0m';
		}
		if (minutes >= 60) {
			const hours = Math.floor(minutes / 60);
			const remainder = minutes % 60;
			return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
		}
		return `${minutes}m`;
	}

	formatItemQuantity = (value) => {
		const quantity = Number(value);
		if (!Number.isFinite(quantity)) {
			return '0';
		}
		return quantity.toLocaleString(undefined, {
			minimumFractionDigits: 0,
			maximumFractionDigits: Number.isInteger(quantity) ? 0 : 4,
		});
	}

	getEntryMinutes = (entry) => {
		if (!entry) {
			return 0;
		}
		if (entry.minutes || entry.minutes === 0) {
			return Number(entry.minutes) || 0;
		}
		if (entry.started_at && entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const ended = Date.parse(entry.ended_at);
			if (!Number.isNaN(started) && !Number.isNaN(ended)) {
				return Math.max(0, Math.round((ended - started) / 60000));
			}
		}
		if (entry.started_at && !entry.ended_at) {
			const started = Date.parse(entry.started_at);
			if (!Number.isNaN(started)) {
				return Math.max(0, Math.round((Date.now() - started) / 60000));
			}
		}
		return 0;
	}

	getStationEntriesByType = (station, type) => {
		const entries = station?.time_entries || [];
		return entries.filter((entry) => `${entry.entry_type}`.toLowerCase() === type && !this.isEntryArchived(entry));
	}

	isEntryArchived = (entry) => {
		if (!entry) {
			return false;
		}
		const value = entry.archived;
		return value === true || value === 1 || value === '1';
	}

	getVisibleTimeEntries = (station) => {
		const entries = station?.time_entries || [];
		return entries.filter((entry) => !this.isEntryArchived(entry));
	}

	getActiveEntry = (station, type) => {
		return this.getStationEntriesByType(station, type).find((entry) => !entry.ended_at) || null;
	}

	getStationMinutesByType = (station, type) => {
		return this.getStationEntriesByType(station, type).reduce((sum, entry) => sum + this.getEntryMinutes(entry), 0);
	}

	getEntrySeconds = (entry) => {
		if (!entry) {
			return 0;
		}
		if (entry.minutes || entry.minutes === 0) {
			return Math.max(0, Math.round((Number(entry.minutes) || 0) * 60));
		}
		const now = this.state.now || Date.now();
		if (entry.started_at && entry.ended_at) {
			const started = Date.parse(entry.started_at);
			const ended = Date.parse(entry.ended_at);
			if (!Number.isNaN(started) && !Number.isNaN(ended)) {
				return Math.max(0, Math.round((ended - started) / 1000));
			}
		}
		if (entry.started_at && !entry.ended_at) {
			const started = Date.parse(entry.started_at);
			if (!Number.isNaN(started)) {
				return Math.max(0, Math.round((now - started) / 1000));
			}
		}
		return 0;
	}

	getStationSecondsByType = (station, type) => {
		return this.getStationEntriesByType(station, type).reduce((sum, entry) => sum + this.getEntrySeconds(entry), 0);
	}

	formatElapsed = (totalSeconds) => {
		const seconds = Math.max(0, Math.round(totalSeconds || 0));
		const h = Math.floor(seconds / 3600);
		const m = Math.floor((seconds % 3600) / 60);
		const s = seconds % 60;
		const pad = (n) => String(n).padStart(2, '0');
		if (h > 0) {
			return `${h}:${pad(m)}:${pad(s)}`;
		}
		return `${m}:${pad(s)}`;
	}

	hasActiveTimers = () => {
		const {jobs, selectedStationKey, selectedJobId} = this.state;
		const selectedJob = jobs.find((job) => job.id === selectedJobId) || null;
		const selectedStation = selectedJob ? this.getStationForJob(selectedJob, selectedStationKey) : null;
		if (!selectedStation) {
			return false;
		}
		return !!this.getActiveEntry(selectedStation, 'labor') || !!this.getActiveEntry(selectedStation, 'machine');
	}

	manageTick = () => {
		if (this.hasActiveTimers()) {
			this.startTick();
		} else {
			this.stopTick();
		}
	}

	startTick = () => {
		if (this._tickInterval) {
			return;
		}
		this._tickInterval = setInterval(() => {
			this.setState({now: Date.now()});
		}, 1000);
	}

	stopTick = () => {
		if (this._tickInterval) {
			clearInterval(this._tickInterval);
			this._tickInterval = null;
		}
	}

	/**
	 * Injects custom (non-model) property names into a BuildClass instance so the
	 * framework's serializer includes them in the POST body.
	 */
	addCustomProperties = (model, keys) => {
		const settable = {settable: true};
		keys.forEach(key => {
			model.properties[key] = settable;
		});
	}

	saveWorkflow = async (jobId, workflow) => {
		try {
			let jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_WORKFLOW');
			this.addCustomProperties(jobModel, ['employee_name']);
			jobModel.object.pkey = jobId;
			jobModel.object.id = jobId;
			jobModel.object.workflow = JSON.stringify(workflow);
			jobModel.parent = this.getAccountParent();
			jobModel.object.status = workflow.job?.status || 'in_progress';
			jobModel.object.employee_name = this.state.employeeName || this.getCurrentUserName() || '';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_WORKFLOW');
			return true;
		} catch (error) {
			API.Toast.error('Failed to update job progress');
			return false;
		}
	}

	recalculateWorkflowStatus = (workflow, now, fallbackQuantity = null) => {
		const stations = workflow.stations || [];
		const jobQuantity = Math.max(
			1,
			Number(workflow.job?.quantity ?? workflow.quantity ?? fallbackQuantity) || 1
		);
		let allStationsComplete = stations.length > 0;
		stations.forEach((station) => {
			const parts = station.parts || [];
			const hasParts = parts.length > 0;
			const isManualComplete = !!station.manual_complete;
			let requiredTotal = 0;
			let completedTotal = 0;
			parts.forEach((part) => {
				const requiredPerUnit = Number(part.required_quantity) || 0;
				const required = requiredPerUnit * jobQuantity;
				const completed = Number(part.completed_quantity) || 0;
				requiredTotal += requiredPerUnit;
				completedTotal += completed;
				if (required > 0 && completed >= required) {
					if (!part.completed_at) {
						part.completed_at = now;
					}
				} else if (part.completed_at && completed < required) {
					part.completed_at = null;
				}
			});
			const wasCompleted = station.status === 'completed';
			const hasActive = this.getVisibleTimeEntries(station).some((entry) => !entry.ended_at);
			const noPartsCompletedUnits = Math.max(0, Number(station.no_parts_completed_units) || 0);
			const clampedNoPartsCompletedUnits = Math.min(jobQuantity, noPartsCompletedUnits);
			station.no_parts_completed_units = clampedNoPartsCompletedUnits;
			const isComplete = hasParts
				? this.isStationComplete(station, jobQuantity)
				: clampedNoPartsCompletedUnits >= jobQuantity || isManualComplete;
			if (isComplete) {
				station.status = 'completed';
				if (!hasParts) {
					station.manual_complete = true;
					station.no_parts_completed_units = jobQuantity;
				}
				if (!station.completed_at || !wasCompleted) {
					station.completed_at = now;
				}
				if (!station.started_at) {
					station.started_at = now;
				}
			} else if (hasParts && (completedTotal > 0 || hasActive)) {
				station.status = 'in_progress';
				if (!station.started_at) {
					station.started_at = now;
				}
				station.completed_at = null;
			} else if (!hasParts && clampedNoPartsCompletedUnits > 0) {
				station.status = 'in_progress';
				station.manual_complete = false;
				station.completed_at = null;
				if (!station.started_at) {
					station.started_at = now;
				}
			} else {
				station.status = 'pending';
				station.manual_complete = false;
				station.completed_at = null;
				if (!hasParts) {
					station.started_at = null;
				}
			}
			station.totals = {
				required_parts: parts.length,
				required_quantity: requiredTotal,
				completed_quantity: completedTotal,
			};
			if (!isComplete) {
				allStationsComplete = false;
			}
		});
		workflow.job = workflow.job || {};
		workflow.job.status = allStationsComplete ? 'completed' : 'in_progress';
		if (allStationsComplete) {
			if (!workflow.job.completed_at) {
				workflow.job.completed_at = now;
			}
		} else {
			workflow.job.completed_at = null;
		}
		return workflow;
	}

	isStationComplete = (station, jobQuantity = 1) => {
		const hasParts = (station.parts || []).length > 0;
		if (!hasParts) {
			return !!station.manual_complete;
		}
		const normalizedQuantity = Math.max(1, Number(jobQuantity) || 1);
		let hasRequired = false;
		const isComplete = (station.parts || []).every((part) => {
			const requiredPerUnit = Number(part.required_quantity) || 0;
			if (requiredPerUnit <= 0) {
				return true;
			}
			hasRequired = true;
			const requiredTotal = requiredPerUnit * normalizedQuantity;
			const completed = Number(part.completed_quantity) || 0;
			return completed >= requiredTotal;
		});
		return hasRequired && isComplete;
	}

	updateWorkflow = async (jobId, updateFn) => {
		const job = this.state.jobs.find((entry) => entry.id === jobId);
		if (!job || !job.workflow) {
			return false;
		}
		const now = new Date().toISOString();
		const workflow = JSON.parse(JSON.stringify(job.workflow));
		const fallbackQuantity = workflow?.job?.quantity
			?? workflow?.quantity
			?? job.quantity
			?? job.job_quantity
			?? job.qty
			?? null;
		if (
			fallbackQuantity !== null
			&& fallbackQuantity !== undefined
			&& `${fallbackQuantity}` !== ''
		) {
			workflow.job = {
				...(workflow.job || {}),
				quantity: fallbackQuantity
			};
		}
		updateFn(workflow, now);
		const updatedWorkflow = this.recalculateWorkflowStatus(workflow, now, fallbackQuantity);
		const saved = await this.saveWorkflow(jobId, updatedWorkflow);
		if (saved) {
			await this.loadJobs();
		}
		return saved;
	}

	handlePartCompletion = async (jobId, stationKey, partKey, delta) => {
		const job = this.state.jobs.find((entry) => entry.id === jobId);
		const station = job ? this.getStationForJob(job, stationKey) : null;
		if (!job?.workflow || !station) {
			return;
		}
		const part = (station.parts || []).find((entry) => `${entry.part_id || entry.part_name}` === partKey);
		if (!part) {
			return;
		}
		const jobQuantity = Math.max(1, Number(job.workflow?.job?.quantity ?? job.workflow?.quantity ?? job.quantity) || 1);
		const current = Number(part.completed_quantity) || 0;
		const requiredPerUnit = Math.max(0, Number(part.required_quantity) || 0);
		const requiredTotal = requiredPerUnit * jobQuantity;
		let next = Math.max(0, current + delta);
		if (delta === 'max_unit') {
			if (requiredPerUnit <= 0) {
				return;
			}
			next = requiredTotal;
		}
		const workflowAutoTransactionType = this.getWorkflowAutoTransactionType(job);
		const serialGateRequired = this.isSerialGateRequired(job, station);
		const completedUnitCount = this.getWorkflowCompletedUnitCount(job.workflow, workflowAutoTransactionType, station);
		const currentUnits = this.getStationCompletedUnits(station, jobQuantity);
		let pendingSerialCompletion = null;

		if (serialGateRequired) {
			const previewStation = JSON.parse(JSON.stringify(station));
			const previewPart = (previewStation.parts || []).find((entry) => `${entry.part_id || entry.part_name}` === partKey);
			if (!previewPart) {
				return;
			}
			previewPart.completed_quantity = next;
			const nextUnits = this.getStationCompletedUnits(previewStation, jobQuantity);
			if (nextUnits < completedUnitCount) {
				API.Toast.error('Cannot reduce completion below the number of serials already created.');
				return;
			}
			const completedUnitDelta = nextUnits - currentUnits;
			if (completedUnitDelta > 1) {
				API.Toast.error('Serial-gated stations can only complete one unit at a time.');
				return;
			}
				if (completedUnitDelta > 0) {
					const serialNumber = this.getSerialInputValue(jobId, station).trim();
					if (!serialNumber) {
						API.Toast.error('Enter a serial number before completing the next unit.');
						return;
					}
					const productDetails = this.buildPendingSerialGateProductDetails(jobId, station, job);
					const productDetailsError = this.getSerialGateProductDetailsError(productDetails);
					if (productDetailsError) {
						API.Toast.error(productDetailsError);
						return;
					}
					pendingSerialCompletion = {
						serialNumber,
						targetUnitNumber: currentUnits + 1,
						productDetails
					};
				}
			}

		const saved = await this.updateWorkflow(jobId, (workflow, now) => {
			const workflowStation = (workflow.stations || []).find((entry) => this.getStationKey(entry) === stationKey);
			if (!workflowStation) {
				return;
			}
			const workflowPart = (workflowStation.parts || []).find((entry) => `${entry.part_id || entry.part_name}` === partKey);
			if (!workflowPart) {
				return;
			}
			workflowPart.completed_quantity = next;
			if (pendingSerialCompletion) {
				workflow.serial_tracking = workflow.serial_tracking || {};
				workflow.serial_tracking.product_details_by_station = {
					...(workflow.serial_tracking.product_details_by_station || {}),
					[this.getSerialGatePersistenceKey(workflowStation)]: {
						...pendingSerialCompletion.productDetails
					}
				};
				this.applyPendingSerialCompletion(
					workflow,
						workflowStation,
						pendingSerialCompletion.serialNumber,
						pendingSerialCompletion.targetUnitNumber,
						now,
						workflowAutoTransactionType,
						pendingSerialCompletion.productDetails
					);
				}
			});

			if (saved && pendingSerialCompletion) {
				this.clearSerialInputValue(jobId, station);
			}
		}

	handleScrapOpen = (partKey) => {
		this.setState((prevState) => ({
			scrapPartKey: prevState.scrapPartKey === partKey ? null : partKey,
			scrapQuantity: '1',
			scrapNote: '',
		}));
	}

	handleScrapCancel = () => {
		this.setState({scrapPartKey: null, scrapQuantity: '1', scrapNote: ''});
	}

	handleScrapItem = async (job, station, part) => {
		if (!station?.job_station_id) {
			API.Toast.error('Station is not initialized. Run the MRP backfill first.');
			return;
		}
		const quantity = Number(this.state.scrapQuantity || 0);
		if (!Number.isFinite(quantity) || quantity <= 0) {
			API.Toast.error('Enter a valid scrap quantity.');
			return;
		}

		const reasonCode = `${this.state.scrapReason || ''}`.trim();
		if (!reasonCode) {
			API.Toast.error('Select a scrap reason.');
			return;
		}

		const locationContext = this.getLocationContext();
		if (!locationContext.location) {
			API.Toast.error('Location is required to log scrap.');
			return;
		}

		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_SCRAP');
			this.addCustomProperties(
				jobModel,
				[
					'job',
					'job_station',
					'part',
					'part_name',
					'item_type',
					'quantity',
					'reason_code',
					'note',
					'employee_name',
					'location',
					'location_binary_id',
				]
			);
			jobModel.parent = this.getAccountParent();
			jobModel.object.job = job?.id || null;
			jobModel.object.job_station = station.job_station_id;
			jobModel.object.part = part?.part_id || null;
			jobModel.object.part_name = part?.part_name || '';
			jobModel.object.item_type = 'part';
			jobModel.object.quantity = quantity;
			jobModel.object.reason_code = reasonCode;
			jobModel.object.note = this.state.scrapNote || '';
			jobModel.object.employee_name = this.state.employeeName || this.getCurrentUserName() || '';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_SCRAP');
			this.setState({scrapPartKey: null, scrapQuantity: '1', scrapNote: ''});
			API.Toast.success('Scrap logged.');
		} catch (error) {
			API.Toast.error('Failed to log scrap');
		}
	}

	handleNoPartsCompletion = async (jobId, stationKey, mode = 'single') => {
		const job = this.state.jobs.find((entry) => entry.id === jobId);
		const station = job ? this.getStationForJob(job, stationKey) : null;
		if (!job?.workflow || !station) {
			return;
		}
		const hasParts = (station.parts || []).length > 0;
		if (hasParts) {
			return;
		}
		const jobQuantity = Math.max(1, Number(job.workflow?.job?.quantity ?? job.workflow?.quantity ?? job.quantity) || 1);
		const current = this.getNoPartsCompletedUnits(station, jobQuantity);
		const next = mode === 'all' ? jobQuantity : Math.min(jobQuantity, current + 1);
		const workflowAutoTransactionType = this.getWorkflowAutoTransactionType(job);
		const serialGateRequired = this.isSerialGateRequired(job, station);
		if (serialGateRequired && mode === 'all' && next > current) {
			API.Toast.error('Serial-gated stations must complete one unit at a time.');
			return;
		}

		let pendingSerialCompletion = null;
		if (serialGateRequired && next > current) {
			const serialNumber = this.getSerialInputValue(jobId, station).trim();
			if (!serialNumber) {
				API.Toast.error('Enter a serial number before completing the next unit.');
				return;
			}
			const productDetails = this.buildPendingSerialGateProductDetails(jobId, station, job);
			const productDetailsError = this.getSerialGateProductDetailsError(productDetails);
			if (productDetailsError) {
				API.Toast.error(productDetailsError);
				return;
			}
			pendingSerialCompletion = {
				serialNumber,
				targetUnitNumber: current + 1,
				productDetails
			};
		}

		const saved = await this.updateWorkflow(jobId, (workflow, now) => {
			const workflowStation = (workflow.stations || []).find((entry) => this.getStationKey(entry) === stationKey);
			if (!workflowStation) {
				return;
			}
			const workflowHasParts = (workflowStation.parts || []).length > 0;
			if (workflowHasParts) {
				return;
			}
			workflowStation.no_parts_completed_units = next;
			workflowStation.started_at = workflowStation.started_at || now;
			if (next >= jobQuantity) {
				workflowStation.manual_complete = true;
				workflowStation.status = 'completed';
				workflowStation.completed_at = now;
			} else if (next > 0) {
				workflowStation.manual_complete = false;
				workflowStation.status = 'in_progress';
				workflowStation.completed_at = null;
			}
			if (pendingSerialCompletion) {
				workflow.serial_tracking = workflow.serial_tracking || {};
				workflow.serial_tracking.product_details_by_station = {
					...(workflow.serial_tracking.product_details_by_station || {}),
					[this.getSerialGatePersistenceKey(workflowStation)]: {
						...pendingSerialCompletion.productDetails
					}
				};
				this.applyPendingSerialCompletion(
					workflow,
						workflowStation,
						pendingSerialCompletion.serialNumber,
						pendingSerialCompletion.targetUnitNumber,
						now,
						workflowAutoTransactionType,
						pendingSerialCompletion.productDetails
					);
				}
			});

			if (saved && pendingSerialCompletion) {
				this.clearSerialInputValue(jobId, station);
			}
		}

	handleStartTimers = async (station, entryType = 'both') => {
		if (!station?.job_station_id) {
			API.Toast.error('Station is not initialized. Run the MRP backfill first.');
			return;
		}
		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_START_TIMERS');
			this.addCustomProperties(jobModel, ['job_station', 'entry_type', 'employee_name']);
			jobModel.parent = this.getAccountParent();
			jobModel.object.job_station = station.job_station_id;
			jobModel.object.entry_type = entryType;
			jobModel.object.employee_name = this.state.employeeName || '';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_START_TIMERS');
			await this.loadJobs();
		} catch (error) {
			API.Toast.error('Failed to start timers');
		}
	}

	handleStopTimers = async (station, entryType = 'both') => {
		if (!station?.job_station_id) {
			API.Toast.error('Station is not initialized. Run the MRP backfill first.');
			return;
		}
		const activePartId = this.getActivePartId(station);
		const hasParts = (station?.parts || []).length > 0;
		if (hasParts && !activePartId) {
			API.Toast.error('Select the active item before stopping timers.');
			return;
		}
		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_STOP_TIMERS');
			this.addCustomProperties(jobModel, ['job_station', 'entry_type', 'job_station_part', 'employee_name']);
			jobModel.parent = this.getAccountParent();
			jobModel.object.job_station = station.job_station_id;
			jobModel.object.entry_type = entryType;
			jobModel.object.employee_name = this.state.employeeName || this.getCurrentUserName() || '';
			if (activePartId) {
				jobModel.object.job_station_part = activePartId;
			}
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_STOP_TIMERS');
			await this.loadJobs();
		} catch (error) {
			API.Toast.error('Failed to stop timers');
		}
	}

	handleManualEntry = async (station) => {
		const minutes = Number(this.state.manualMinutes);
		if (!minutes || minutes <= 0) {
			API.Toast.error('Enter minutes worked.');
			return;
		}
		if (!station?.job_station_id) {
			API.Toast.error('Station is not initialized. Run the MRP backfill first.');
			return;
		}
		const activePartId = this.getActivePartId(station);
		if (!activePartId) {
			API.Toast.error('Select the active item before adding time.');
			return;
		}
		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_MANUAL_TIME');
			this.addCustomProperties(jobModel, ['job_station', 'entry_type', 'minutes', 'employee_name', 'note', 'job_station_part']);
			jobModel.parent = this.getAccountParent();
			jobModel.object.job_station = station.job_station_id;
			jobModel.object.entry_type = this.state.manualType;
			jobModel.object.minutes = minutes;
			jobModel.object.employee_name = this.state.employeeName || '';
			jobModel.object.note = this.state.manualNote || '';
			jobModel.object.job_station_part = activePartId;
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_MANUAL_TIME');
			this.setState({manualMinutes: '', manualNote: ''});
			await this.loadJobs();
		} catch (error) {
			API.Toast.error('Failed to add manual time');
		}
	}

	handleEditStart = (entry) => {
		const totalSeconds = this.getEntrySeconds(entry);
		const entryMinutes = Math.floor(totalSeconds / 60);
		const entrySeconds = totalSeconds % 60;
		this.setState({
			editingEntryId: entry.time_entry_id,
			editMinutes: `${entryMinutes}`,
			editSeconds: `${entrySeconds}`,
			editNote: entry.note || '',
		});
	}

	handleEditCancel = () => {
		this.setState({
			editingEntryId: null,
			editMinutes: '',
			editSeconds: '',
			editNote: '',
		});
	}

	handleEditSave = async (entry) => {
		const minutesPart = Number(this.state.editMinutes || 0);
		const secondsPart = Number(this.state.editSeconds || 0);
		if (!Number.isFinite(minutesPart) || minutesPart < 0 || !Number.isFinite(secondsPart) || secondsPart < 0) {
			API.Toast.error('Enter a valid time.');
			return;
		}
		const normalizedMinutes = Math.floor(minutesPart);
		const normalizedSeconds = Math.floor(secondsPart);
		const totalSeconds = (normalizedMinutes * 60) + normalizedSeconds;
		if (totalSeconds <= 0) {
			API.Toast.error('Enter minutes or seconds worked.');
			return;
		}
		const minutes = Math.round((totalSeconds / 60) * 100) / 100;
		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_UPDATE_TIME_ENTRY');
			this.addCustomProperties(jobModel, ['time_entry', 'minutes', 'note', 'employee_name']);
			jobModel.parent = this.getAccountParent();
			jobModel.object.time_entry = entry.time_entry_id;
			jobModel.object.minutes = minutes;
			jobModel.object.note = this.state.editNote || '';
			jobModel.object.employee_name = this.state.employeeName || '';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_UPDATE_TIME_ENTRY');
			this.handleEditCancel();
			await this.loadJobs();
		} catch (error) {
			API.Toast.error('Failed to update time entry');
		}
	}

	handleEditDelete = async (entry) => {
		if (!entry?.time_entry_id) {
			API.Toast.error('Missing time entry.');
			return;
		}
		if (!window.confirm('Archive this time entry?')) {
			return;
		}
		try {
			const jobModel = new API.BuildClass(Types.Mrp_Jobs._name, 'POST_DELETE_TIME_ENTRY');
			this.addCustomProperties(jobModel, ['time_entry', 'employee_name']);
			jobModel.parent = this.getAccountParent();
			jobModel.object.time_entry = entry.time_entry_id;
			jobModel.object.employee_name = this.state.employeeName || '';
			applyMrpLocationToRequest(jobModel, this.props.rootState);
			await jobModel.submit(null, 'POST_DELETE_TIME_ENTRY');
			this.handleEditCancel();
			await this.loadJobs();
		} catch (error) {
			API.Toast.error('Failed to archive time entry');
		}
	}

	handleJobViewModeChange = (nextMode) => {
		this.setState((prevState) => {
			const stationKey = prevState.selectedStationKey;
			let softCompletedJobs = prevState.softCompletedJobs;
			if (nextMode === 'completed' && stationKey && softCompletedJobs[stationKey]) {
				softCompletedJobs = {
					...softCompletedJobs,
					[stationKey]: {},
				};
			}
			const jobsForMode = nextMode === 'completed'
				? this.getCompletedJobsForStation(prevState.jobs, stationKey)
				: this.getJobsForStation(prevState.jobs, stationKey);
			const allStationJobs = this.getAllJobsForStation(prevState.jobs, stationKey);
			const selectedJobId = prevState.selectedJobId && allStationJobs.some((job) => job.id === prevState.selectedJobId)
				? prevState.selectedJobId
				: (jobsForMode[0]?.id || allStationJobs[0]?.id || '');
			return {
				jobViewMode: nextMode,
				softCompletedJobs,
				selectedJobId,
			};
		});
	}

	render() {
		const {
			loading,
			loadError,
			jobs,
			selectedStationKey,
			selectedJobId,
			jobViewMode,
			softCompletedJobs,
			employeeName,
			scrapReason,
			scrapQuantity,
			scrapNote,
			scrapPartKey,
			manualType,
			manualMinutes,
			manualNote,
			editingEntryId,
			editMinutes,
			editSeconds,
			editNote
		} = this.state;

		const stationOptions = this.getStationOptions(jobs);
		const availableJobsForStation = this.getJobsForStation(jobs, selectedStationKey);
		const completedJobsForStation = this.getCompletedJobsForStation(jobs, selectedStationKey);
		const jobsForStation = jobViewMode === 'completed' ? completedJobsForStation : availableJobsForStation;
		const selectedStationJobs = this.getAllJobsForStation(jobs, selectedStationKey);
		const selectedJob = selectedStationJobs.find((job) => job.id === selectedJobId) || null;
		const selectedStation = selectedJob ? this.getStationForJob(selectedJob, selectedStationKey) : null;
		const selectedJobCompleted = selectedJob ? this.isJobCompleteForStation(selectedJob, selectedStationKey) : false;
		const selectedJobSoftCompleted = !!(selectedStationKey && selectedJob && softCompletedJobs?.[selectedStationKey]?.[selectedJob.id]);
		const activePartId = selectedStation ? this.getActivePartId(selectedStation) : null;
		const hasParts = (selectedStation?.parts || []).length > 0;
		const jobQuantity = Math.max(1, Number(selectedJob?.workflow?.job?.quantity ?? selectedJob?.quantity) || 1);
		const stationCompletedUnits = selectedStation ? this.getStationCompletedUnits(selectedStation, jobQuantity) : 0;
		const noPartsCompletedUnits = !hasParts
			? this.getNoPartsCompletedUnits(selectedStation, jobQuantity)
			: 0;
		const noPartsRemaining = Math.max(0, jobQuantity - noPartsCompletedUnits);
		const noPartsPercent = jobQuantity > 0
			? Math.min(100, Math.round((Math.min(jobQuantity, noPartsCompletedUnits) / jobQuantity) * 100))
			: 0;
		const workflowAutoTransactionType = selectedJob && selectedStation ? this.getWorkflowAutoTransactionType(selectedJob) : '';
		const serialGateRequired = selectedJob && selectedStation ? this.isSerialGateRequired(selectedJob, selectedStation) : false;
		const serialGateInput = serialGateRequired && selectedJob && selectedStation
			? this.getSerialInputValue(selectedJob.id, selectedStation)
			: '';
		const serialGateProductDetails = serialGateRequired && selectedJob && selectedStation
			? this.getSerialGateProductDetails(selectedJob.id, selectedStation, selectedJob)
			: this.getEmptySerialGateProductDetails();
		const completedUnitCount = serialGateRequired && selectedJob && selectedStation
			? this.getWorkflowCompletedUnitCount(selectedJob.workflow, workflowAutoTransactionType, selectedStation)
			: 0;
		const nextSerialUnitNumber = Math.min(jobQuantity, Math.max(completedUnitCount, stationCompletedUnits) + 1);
		const workflowDealerName = this.getWorkflowAutoTransactionDealerName(selectedJob);
		const workflowActionPastTense = workflowAutoTransactionType === 'transfer' ? 'Transferred' : 'Acquired';
		const workflowCompletionMessage = workflowAutoTransactionType === 'transfer'
			? 'Completion at this station will create the product record from these serial-gate details and attach it to the ERP transfer.'
			: 'Completion at this station will create the product record from these serial-gate details and attach it to the ERP acquisition.';
		const laborActive = selectedStation ? this.getActiveEntry(selectedStation, 'labor') : null;
		const machineActive = selectedStation ? this.getActiveEntry(selectedStation, 'machine') : null;
		const selectedStationLabel = stationOptions.find((option) => option.key === selectedStationKey)?.label || 'No station selected';
		const totalOpenStationJobs = availableJobsForStation.filter((job) => !this.isJobCompleteForStation(job, selectedStationKey)).length;
		const totalActiveTimers = Number(Boolean(laborActive)) + Number(Boolean(machineActive));

		return (
			<div className="row">
				<div className="col-12 mb-3">
					<div style={stationWorkStyles.heroBand}>
						<div className="row align-items-center">
							<div className="col-lg-8">
								<div style={stationWorkStyles.heroEyebrow}>ERP Work</div>
								<h2 style={stationWorkStyles.heroTitle}>Run Station Execution and Time Capture</h2>
								<div style={stationWorkStyles.heroSubtext}>
									Use ERP work controls to start and stop timers, post manual time, record scrap, and complete items or units at the selected station.
								</div>
							</div>
							<div className="col-lg-4 mt-3 mt-lg-0">
								<div style={stationWorkStyles.heroStatsWrap}>
									<div style={stationWorkStyles.heroStatRow}><span style={stationWorkStyles.heroStatLabel}>Station</span><strong style={stationWorkStyles.heroStatValue}>{selectedStationLabel}</strong></div>
									<div style={stationWorkStyles.heroStatRow}><span style={stationWorkStyles.heroStatLabel}>Open Jobs</span><strong style={stationWorkStyles.heroStatValue}>{totalOpenStationJobs}</strong></div>
									<div style={stationWorkStyles.heroStatRow}><span style={stationWorkStyles.heroStatLabel}>Active Timers</span><strong style={stationWorkStyles.heroStatValue}>{totalActiveTimers}</strong></div>
									<div className="mt-2">
											<MrpLocationControl
												rootState={this.props.rootState}
												id="mrp-station-work-location"
												selectStyle={stationWorkStyles.heroSelect}
												note={LOCATION_NOTE}
											/>
										</div>
									</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-lg-4">
					<Card style={stationWorkStyles.cardShell}>
						<CardHeader style={stationWorkStyles.cardHeader}>Station Login</CardHeader>
						<CardBody>
								<FormGroup>
									<Label for="employeeName">Employee Name</Label>
									<Input
									id="employeeName"
									value={employeeName}
									onChange={(event) => {
										const value = event.target.value;
										this.setState({employeeName: value});
										this.setStoredEmployeeName(value);
									}}
										placeholder="Enter your name"
									/>
									<div className="small text-muted mt-2">
										This name is reused on this browser for ERP time, scrap, and audit log actions until you change it.
									</div>
								</FormGroup>
							<FormGroup>
								<Label for="stationSelect">Station</Label>
								<Input
									id="stationSelect"
									type="select"
									value={selectedStationKey}
									onChange={(event) => {
										const key = event.target.value;
										const stationJobs = this.state.jobViewMode === 'completed'
											? this.getCompletedJobsForStation(jobs, key)
											: this.getJobsForStation(jobs, key);
										const allStationJobs = this.getAllJobsForStation(jobs, key);
										this.setState({
											selectedStationKey: key,
											selectedJobId: stationJobs[0]?.id || allStationJobs[0]?.id || '',
										});
									}}
								>
									<option value="">Select a station</option>
									{stationOptions.map((option) => (
										<option key={option.key} value={option.key}>{option.label}</option>
									))}
								</Input>
							</FormGroup>
							{loadError && <div className="text-danger">{loadError}</div>}
							{loading && <div>Loading jobs...</div>}
						</CardBody>
					</Card>

					<Card className="mt-3" style={stationWorkStyles.cardShell}>
						<CardHeader style={stationWorkStyles.cardHeader}>Jobs</CardHeader>
						<CardBody>
							<div className="d-flex mb-3" style={stationWorkStyles.jobsToggleGroup}>
								<Button
									size="sm"
									color={jobViewMode === 'available' ? 'primary' : 'secondary'}
									onClick={() => this.handleJobViewModeChange('available')}
									style={stationWorkStyles.jobsToggleButton}
								>
									Available ({availableJobsForStation.length})
								</Button>
								<Button
									size="sm"
									color={jobViewMode === 'completed' ? 'primary' : 'secondary'}
									onClick={() => this.handleJobViewModeChange('completed')}
									style={stationWorkStyles.jobsToggleButton}
								>
									Completed ({completedJobsForStation.length})
								</Button>
							</div>
							{!selectedStationKey && <div>Select a station to view jobs.</div>}
							{selectedStationKey && jobsForStation.length === 0 && (
								<div>{jobViewMode === 'completed' ? 'No completed jobs for this station.' : 'No available jobs for this station.'}</div>
							)}
							{jobsForStation.map((job) => (
								<div key={job.id} className="d-flex align-items-center justify-content-between mb-2">
									<div>
										<div className="d-flex align-items-center">
											<strong>{job.name}</strong>
											{this.isJobCompleteForStation(job, selectedStationKey) && (
												<Badge className="ml-2" color="success">Completed</Badge>
											)}
										</div>
										<div className="text-muted">{job.modelName}</div>
									</div>
									<Button
										color={job.id === selectedJobId ? 'primary' : 'secondary'}
										style={stationWorkStyles.pageActionButton}
										onClick={() => this.setState({selectedJobId: job.id})}
									>
										Select
									</Button>
								</div>
							))}
						</CardBody>
					</Card>
				</div>

				<div className="col-lg-8">
					{selectedJob && selectedStation ? (
						<>
							<Card style={stationWorkStyles.cardShell}>
								<CardHeader style={stationWorkStyles.cardHeader}>
									<div className="d-flex align-items-center justify-content-between">
										<div>
											<div><strong>{selectedJob.name}</strong></div>
											<div className="text-muted">{selectedStation.station_name || 'Station'}</div>
										</div>
										<Badge color={this.isStatusComplete(selectedStation.status) ? 'success' : 'primary'}>
											{selectedStation.status || 'In Progress'}
										</Badge>
									</div>
								</CardHeader>
								<CardBody>
									{selectedJobCompleted && (
										<div className="mb-3" style={stationWorkStyles.completedJobNotice}>
											This job is completed for this station.
											{jobViewMode === 'available' && selectedJobSoftCompleted}
										</div>
									)}
									<div className="row mb-3">
										<div className="col-md-6">
											<h6>Labor</h6>
											{laborActive ? (
												<div className="mb-1" style={{fontSize: '1.5rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums'}}>
													{this.formatElapsed(this.getEntrySeconds(laborActive))}
												</div>
											) : (
												<div className="text-muted mb-1">Stopped</div>
											)}
											<div className="text-muted mb-2" style={{fontVariantNumeric: 'tabular-nums'}}>
												Total: {this.formatElapsed(this.getStationSecondsByType(selectedStation, 'labor'))}
											</div>
											{laborActive ? (
												<Button color="warning" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStopTimers(selectedStation, 'labor')}>
													Stop Labor
												</Button>
											) : (
												<Button color="success" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStartTimers(selectedStation, 'labor')}>
													Start Labor
												</Button>
											)}
										</div>
										<div className="col-md-6">
											<h6>Machine</h6>
											{machineActive ? (
												<div className="mb-1" style={{fontSize: '1.5rem', fontWeight: 'bold', fontVariantNumeric: 'tabular-nums'}}>
													{this.formatElapsed(this.getEntrySeconds(machineActive))}
												</div>
											) : (
												<div className="text-muted mb-1">Stopped</div>
											)}
											<div className="text-muted mb-2" style={{fontVariantNumeric: 'tabular-nums'}}>
												Total: {this.formatElapsed(this.getStationSecondsByType(selectedStation, 'machine'))}
											</div>
											{machineActive ? (
												<Button color="warning" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStopTimers(selectedStation, 'machine')}>
													Stop Machine
												</Button>
											) : (
												<Button color="success" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStartTimers(selectedStation, 'machine')}>
													Start Machine
												</Button>
											)}
										</div>
									</div>
									<div className="d-flex flex-wrap">
										{(!laborActive || !machineActive) && (
											<Button color="success" className="mr-2" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStartTimers(selectedStation, 'both')}>
												Start Both
											</Button>
										)}
										{(laborActive || machineActive) && (
											<Button color="warning" style={stationWorkStyles.pageActionButton} onClick={() => this.handleStopTimers(selectedStation, 'both')}>
												Stop All
											</Button>
										)}
									</div>
								</CardBody>
							</Card>

							<Card className="mt-3" style={stationWorkStyles.cardShell}>
								<CardHeader style={stationWorkStyles.cardHeader}>Active Item</CardHeader>
								<CardBody>
									{(selectedStation.parts || []).length === 0 ? (
										<div>
											<div className="d-flex align-items-center justify-content-between flex-wrap">
												<div className="text-muted mb-2 mb-md-0">No items available for this station.</div>
												<div>
													<Button
														color="primary"
														className="mr-2"
														style={stationWorkStyles.pageActionButton}
														onClick={() => this.handleNoPartsCompletion(selectedJob.id, selectedStationKey, 'single')}
														disabled={noPartsRemaining <= 0}
													>
														Complete 1 Unit
													</Button>
													<Button
														color="success"
														style={stationWorkStyles.pageActionButton}
														onClick={() => this.handleNoPartsCompletion(selectedJob.id, selectedStationKey, 'all')}
														disabled={noPartsRemaining <= 0 || serialGateRequired}
													>
														Complete All Units
													</Button>
												</div>
											</div>
											<div className="small text-muted mt-2">
												Use this for QC/inspection stations that do not consume items. Completed: {Math.min(jobQuantity, noPartsCompletedUnits)} / {jobQuantity}.
											</div>
											<div className="mt-2">
												<div className="d-flex justify-content-between small text-muted mb-1">
													<span>Progress</span>
													<span>{Math.min(jobQuantity, noPartsCompletedUnits)} / {jobQuantity}</span>
												</div>
												<div className="progress" style={{height: '8px'}}>
													<div
														className="progress-bar bg-success"
														role="progressbar"
														style={{width: `${noPartsPercent}%`}}
														aria-valuenow={noPartsPercent}
														aria-valuemin="0"
														aria-valuemax="100"
													/>
												</div>
											</div>
										</div>
									) : (
											<FormGroup>
												<Label for="activePart">Select active item</Label>
												<Input
												id="activePart"
												type="select"
												value={activePartId || ''}
												onChange={(event) => {
													const nextId = event.target.value ? Number(event.target.value) : null;
													this.setState((prevState) => ({
														activePartByStation: {
															...prevState.activePartByStation,
															[selectedStation.job_station_id]: nextId
														}
													}));
												}}
											>
													{(selectedStation.parts || []).map((part) => (
														<option key={part.job_station_part_id} value={part.job_station_part_id}>
															{part.part_name || `Item ${part.part_id || part.job_station_part_id}`}
														</option>
													))}
												</Input>
												<div className="small text-muted mt-2">
													Stop-timer and manual-time actions post against the active item selected here for this station.
												</div>
											</FormGroup>
										)}
										{serialGateRequired && (
											<div className="border rounded p-3 mt-3" style={{backgroundColor: '#fbfcfd', borderColor: '#d9e2ec'}}>
												<div className="d-flex align-items-center justify-content-between flex-wrap">
												<div>
													<div className="font-weight-bold">Serial Gate</div>
													<div className="small text-muted">
														Unit {Math.min(jobQuantity, nextSerialUnitNumber)} of {jobQuantity}
														{workflowDealerName ? ` • ${workflowDealerName}` : ''}
													</div>
												</div>
													<div className="small text-muted">
														{workflowActionPastTense}: {completedUnitCount} / {jobQuantity}
													</div>
												</div>
												<div className="row mt-3">
													<div className="col-md-6">
														<FormGroup className="mb-0">
															<Label for="serialGateInput">Serial Number</Label>
															<Input
																id="serialGateInput"
																value={serialGateInput}
																onChange={(event) => this.setSerialInputValue(selectedJob.id, selectedStation, event.target.value)}
																placeholder="Scan or enter the serial for the next completed unit"
															/>
														</FormGroup>
													</div>
													<div className="col-md-6">
														<FormGroup className="mb-0">
															<Label for="serialGateUpc">UPC</Label>
															<Input
																id="serialGateUpc"
																value={serialGateProductDetails.upc}
																onChange={(event) => this.setSerialGateProductField(selectedJob.id, selectedStation, 'upc', event.target.value)}
																placeholder="Enter UPC"
															/>
														</FormGroup>
													</div>
												</div>
												<div className="row mt-3">
													<div className="col-md-6">
														<FormGroup className="mb-0">
															<Label for="serialGateManufacturer">Manufacturer</Label>
															<Input
																id="serialGateManufacturer"
																type="select"
																value={serialGateProductDetails.manufacturer}
																onChange={this.handleSerialGateLookupChange(selectedJob.id, selectedStation, 'manufacturer')}
															>
																<option value="">Other (Manual Entry)</option>
																{this.state.manufacturers.map((manufacturer) => (
																	<option key={manufacturer.id} value={manufacturer.id}>
																		{manufacturer.label}
																	</option>
																))}
															</Input>
															{!serialGateProductDetails.manufacturer && (
																<Input
																	className="mt-2"
																	value={serialGateProductDetails.manufacturer_other}
																	onChange={(event) => this.setSerialGateProductField(selectedJob.id, selectedStation, 'manufacturer_other', event.target.value)}
																	placeholder="Manufacturer (manual entry)"
																/>
															)}
														</FormGroup>
													</div>
													<div className="col-md-6">
														<FormGroup className="mb-0">
															<Label for="serialGateCaliber">Caliber</Label>
															<Input
																id="serialGateCaliber"
																type="select"
																value={serialGateProductDetails.caliber}
																onChange={this.handleSerialGateLookupChange(selectedJob.id, selectedStation, 'caliber')}
															>
																<option value="">Other (Manual Entry)</option>
																{this.state.calibers.map((caliber) => (
																	<option key={caliber.id} value={caliber.id}>
																		{caliber.label}
																	</option>
																))}
															</Input>
															{!serialGateProductDetails.caliber && (
																<Input
																	className="mt-2"
																	value={serialGateProductDetails.caliber_other}
																	onChange={(event) => this.setSerialGateProductField(selectedJob.id, selectedStation, 'caliber_other', event.target.value)}
																	placeholder="Caliber (manual entry)"
																/>
															)}
														</FormGroup>
													</div>
												</div>
												<div className="row mt-3">
													<div className="col-md-6">
														<FormGroup className="mb-0">
															<Label for="serialGateType">Type</Label>
															<Input
																id="serialGateType"
																type="select"
																value={serialGateProductDetails.type}
																onChange={(event) => this.setSerialGateProductField(selectedJob.id, selectedStation, 'type', event.target.value)}
															>
																<option value="">Select a type</option>
																{this.state.productTypes.map((type) => (
																	<option key={type.value} value={type.value}>
																		{type.label}
																	</option>
																))}
															</Input>
														</FormGroup>
													</div>
												</div>
												<div className="small text-muted mt-2">
													{workflowCompletionMessage}
												</div>
											</div>
									)}
								</CardBody>
							</Card>

								<Card className="mt-3" style={stationWorkStyles.cardShell}>
									<CardHeader style={stationWorkStyles.cardHeader}>Items</CardHeader>
									<CardBody>
										{(selectedStation.parts || []).length === 0 && (
											<div className="text-muted">No items for this station.</div>
										)}
										{(selectedStation.parts || []).map((part) => {
											const partKey = `${part.part_id || part.part_name}`;
											const requiredPerUnit = Number(part.required_quantity) || 0;
											const jobQuantity = Math.max(1, Number(selectedJob.quantity) || 1);
											const requiredTotal = requiredPerUnit * jobQuantity;
											const completed = Number(part.completed_quantity) || 0;
											const requiredPerUnitLabel = this.formatItemQuantity(requiredPerUnit);
											const requiredTotalLabel = this.formatItemQuantity(requiredTotal);
											const completedLabel = this.formatItemQuantity(completed);
											const canDecrease = completed > 0;
											const canMax = requiredPerUnit > 0 && requiredTotal > completed;
											const isScrapOpen = scrapPartKey === partKey;
											return (
												<React.Fragment key={partKey}>
													<div className="d-flex align-items-center border-bottom py-2" style={stationWorkStyles.itemRow}>
														<div style={stationWorkStyles.itemRowLeft}>
															<Button
																color="danger"
																aria-label={`Scrap ${part.part_name || 'item'}`}
																style={stationWorkStyles.pageActionButton}
																onClick={() => this.handleScrapOpen(partKey)}
															>
																Scrap
															</Button>
														</div>
														<div style={stationWorkStyles.itemRowCenter}>
															<div><strong>{part.part_name}</strong></div>
															<div className="text-muted">
																{completedLabel} / {requiredTotalLabel}
															</div>
															<div className="small text-muted mt-1">
																Required per unit: {requiredPerUnitLabel}
															</div>
														</div>
														<div className="d-flex align-items-center justify-content-end" style={stationWorkStyles.itemRowRight}>
															<Button
																color="secondary"
																aria-label={`Decrease ${part.part_name} quantity`}
																style={stationWorkStyles.partQtyButton}
																disabled={!canDecrease}
																onClick={() => this.handlePartCompletion(selectedJob.id, selectedStationKey, partKey, -1)}
															>
																-
															</Button>
															<Button
																color="primary"
																aria-label={`Increase ${part.part_name} quantity`}
																style={stationWorkStyles.partQtyButton}
																onClick={() => this.handlePartCompletion(selectedJob.id, selectedStationKey, partKey, 1)}
															>
																+
															</Button>
															<Button
																color="success"
																aria-label={`Fill ${part.part_name} to max required quantity`}
																style={stationWorkStyles.partQtyMaxButton}
																disabled={!canMax}
																onClick={() => this.handlePartCompletion(selectedJob.id, selectedStationKey, partKey, 'max_unit')}
															>
																Max
															</Button>
														</div>
													</div>
													{isScrapOpen && (
														<div className="border rounded p-2 mb-2" style={stationWorkStyles.scrapControlBox}>
															<div className="small text-muted mb-2">Scrap {part.part_name}</div>
															<div className="row">
																<div className="col-md-4">
																	<Label className="mb-1" for={`scrapReason_${partKey}`}>Reason</Label>
																	<Input
																		id={`scrapReason_${partKey}`}
																		type="select"
																		value={scrapReason}
																		onChange={(event) => this.setState({scrapReason: event.target.value})}
																	>
																		{SCRAP_REASON_OPTIONS.map((option) => (
																			<option key={option.value} value={option.value}>{option.label}</option>
																		))}
																	</Input>
																</div>
																<div className="col-md-2">
																	<Label className="mb-1" for={`scrapQuantity_${partKey}`}>Qty</Label>
																	<Input
																		id={`scrapQuantity_${partKey}`}
																		type="number"
																		min="0.0001"
																		step="0.0001"
																		value={scrapQuantity}
																		onChange={(event) => this.setState({scrapQuantity: event.target.value})}
																	/>
																</div>
																<div className="col-md-4">
																	<Label className="mb-1" for={`scrapNote_${partKey}`}>Note</Label>
																	<Input
																		id={`scrapNote_${partKey}`}
																		value={scrapNote}
																		onChange={(event) => this.setState({scrapNote: event.target.value})}
																		placeholder="Optional note"
																	/>
																</div>
																<div className="col-md-2 d-flex align-items-end" style={stationWorkStyles.scrapEditorActions}>
																	<Button
																		color="danger"
																		style={stationWorkStyles.pageActionButtonSmall}
																		onClick={() => this.handleScrapItem(selectedJob, selectedStation, part)}
																	>
																		Confirm
																	</Button>
																	<Button
																		color="secondary"
																		style={stationWorkStyles.pageActionButtonSmall}
																		onClick={this.handleScrapCancel}
																	>
																		Cancel
																	</Button>
																</div>
															</div>
														</div>
													)}
												</React.Fragment>
											);
										})}
									</CardBody>
								</Card>

								<Card className="mt-3" style={stationWorkStyles.cardShell}>
									<CardHeader style={stationWorkStyles.cardHeader}>Manual Time Entry</CardHeader>
									<CardBody>
										<div className="small text-muted mb-3">
											Manual time uses the selected employee name and, for item-based stations, posts against the active item shown above.
										</div>
										<div className="row">
										<div className="col-md-4">
											<FormGroup>
												<Label for="manualType">Type</Label>
												<Input
													id="manualType"
													type="select"
													value={manualType}
													onChange={(event) => this.setState({manualType: event.target.value})}
												>
													<option value="labor">Labor</option>
													<option value="machine">Machine</option>
												</Input>
											</FormGroup>
										</div>
										<div className="col-md-4">
											<FormGroup>
												<Label for="manualMinutes">Minutes</Label>
												<Input
													id="manualMinutes"
													type="number"
													min="0"
													value={manualMinutes}
													onChange={(event) => this.setState({manualMinutes: event.target.value})}
												/>
											</FormGroup>
										</div>
										<div className="col-md-4">
											<FormGroup>
												<Label for="manualNote">Note</Label>
												<Input
													id="manualNote"
													value={manualNote}
													onChange={(event) => this.setState({manualNote: event.target.value})}
												/>
											</FormGroup>
										</div>
									</div>
									<Button color="primary" style={stationWorkStyles.pageActionButton} onClick={() => this.handleManualEntry(selectedStation)}>
										Add Manual Time
									</Button>
								</CardBody>
							</Card>

								<Card className="mt-3" style={stationWorkStyles.cardShell}>
									<CardHeader style={stationWorkStyles.cardHeader}>Time Entries</CardHeader>
									<CardBody>
										<div className="small text-muted mb-3">
											Only manual or stopped entries can be edited. Delete archives the entry from ERP views instead of removing it permanently.
										</div>
										{this.getVisibleTimeEntries(selectedStation).length === 0 && (
											<div className="text-muted">No time entries yet.</div>
									)}
									{this.getVisibleTimeEntries(selectedStation).map((entry) => {
										const isEditing = editingEntryId === entry.time_entry_id;
										const canEditEntry = !!entry.manual || !!entry.ended_at;
										const employeeNameForEntry = entry.employee_name || entry.created_by_name || 'Unknown';
										return (
											<div key={entry.time_entry_id} className="border rounded p-2 mb-2" style={stationWorkStyles.entryCard}>
												<div className="d-flex align-items-center justify-content-between">
													<div>
														<strong>{entry.entry_type}</strong>
														{' '}• <span style={{fontVariantNumeric: 'tabular-nums'}}>{this.formatElapsed(this.getEntrySeconds(entry))}</span>
														{' '}• <span className="text-muted">{employeeNameForEntry}</span>
														{!entry.ended_at && !entry.manual && <Badge className="ml-2" color="info">Active</Badge>}
														{entry.manual && <Badge className="ml-2" color="secondary">Manual</Badge>}
													</div>
													{canEditEntry && !isEditing && (
														<Button size="sm" color="secondary" style={stationWorkStyles.pageActionButtonSmall} onClick={() => this.handleEditStart(entry)}>
															Edit
														</Button>
													)}
												</div>
												{entry.note && <div className="text-muted">{entry.note}</div>}
													{isEditing && (
														<div className="row mt-2">
															<div className="col-md-3">
																<Label className="mb-1">Minutes</Label>
																<Input
																	type="number"
																	min="0"
																	value={editMinutes}
																	onChange={(event) => this.setState({editMinutes: event.target.value})}
																/>
															</div>
															<div className="col-md-2">
																<Label className="mb-1">Seconds</Label>
																<Input
																	type="number"
																	min="0"
																	max="59"
																	value={editSeconds}
																	onChange={(event) => this.setState({editSeconds: event.target.value})}
																/>
															</div>
															<div className="col-md-4">
																<Label className="mb-1">Note</Label>
																<Input
																	value={editNote}
																	onChange={(event) => this.setState({editNote: event.target.value})}
															/>
														</div>
														<div className="col-md-3 d-flex">
															<Button size="sm" color="primary" style={stationWorkStyles.pageActionButtonSmall} onClick={() => this.handleEditSave(entry)}>
																Save
															</Button>
															<Button size="sm" color="danger" className="ml-2" style={stationWorkStyles.pageActionButtonSmall} onClick={() => this.handleEditDelete(entry)}>
																Delete
															</Button>
															<Button size="sm" color="secondary" className="ml-2" style={stationWorkStyles.pageActionButtonSmall} onClick={this.handleEditCancel}>
																Cancel
															</Button>
														</div>
													</div>
												)}
											</div>
										);
									})}
								</CardBody>
							</Card>

							</>
						) : (
						<Card style={stationWorkStyles.cardShell}>
							<CardBody>
								Select a station and job to begin work.
							</CardBody>
						</Card>
					)}
				</div>
			</div>
		);
	}
}

const stationWorkStyles = {
	heroBand: {
		background: 'linear-gradient(135deg, #eef6ff 0%, #fafcff 44%, #e3f4f4 100%)',
		border: '1px solid #d6e2f0',
		borderTop: '4px solid #248494',
		borderRadius: '14px',
		padding: '22px 24px',
		color: '#17324d',
		boxShadow: '0 10px 24px rgba(12, 55, 92, 0.08)',
	},
	heroEyebrow: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: '999px',
		backgroundColor: '#dff3f5',
		color: '#1f7481',
		fontSize: '11px',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		fontWeight: 700,
	},
	heroTitle: {
		fontSize: '27px',
		lineHeight: 1.2,
		margin: '8px 0 10px',
		fontWeight: 700,
	},
	heroSubtext: {
		fontSize: '14px',
		color: '#557086',
		maxWidth: '540px',
	},
	heroStatsWrap: {
		background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, #ecf8f8 100%)',
		border: '1px solid #d7e4f2',
		borderRadius: '12px',
		padding: '12px 14px',
		boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78)',
	},
	heroSelect: {
		backgroundColor: '#ffffff',
		border: '1px solid #c9d9e8',
		color: '#17324d',
	},
	heroStatRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		fontSize: '13px',
		padding: '7px 0',
		borderBottom: '1px solid #e3ecf5',
		gap: '8px',
	},
	heroStatLabel: {
		color: '#5f7489',
	},
	heroStatValue: {
		color: '#17324d',
		fontSize: '15px',
		fontWeight: 700,
	},
	cardShell: {
		borderRadius: '12px',
		border: '1px solid #d6e2f0',
		boxShadow: '0 8px 18px rgba(12, 55, 92, 0.09)',
		overflow: 'hidden',
	},
	cardHeader: {
		background: 'linear-gradient(90deg, #f8fbff 0%, #edf4ff 100%)',
		borderBottom: '1px solid #d7e4f4',
		fontWeight: 600,
	},
	entryCard: {
		backgroundColor: '#f9fcff',
		borderColor: '#dce8f6',
	},
	jobsToggleGroup: {
		gap: '8px',
	},
	jobsToggleButton: {
		...ACTION_BUTTON_BASE,
		flex: 1,
		fontSize: '0.92rem',
	},
	pageActionButton: {
		...ACTION_BUTTON_BASE,
	},
	pageActionButtonSmall: {
		...ACTION_BUTTON_SMALL,
	},
	completedJobNotice: {
		padding: '10px 12px',
		border: '1px solid #9bd6a8',
		borderRadius: '8px',
		backgroundColor: '#eaf8ee',
		color: '#1e5f30',
		fontSize: '0.92rem',
	},
	partQtyControls: {
		gap: '10px',
	},
	scrapControlBox: {
		backgroundColor: '#fff8f8',
		borderColor: '#f0d7d7',
	},
	scrapEditorActions: {
		gap: '8px',
	},
	itemRow: {
		gap: '12px',
	},
	itemRowLeft: {
		minWidth: '112px',
	},
	itemRowCenter: {
		flex: 1,
		textAlign: 'center',
	},
	itemRowRight: {
		minWidth: '188px',
		gap: '10px',
	},
	partQtyButton: {
		...ACTION_BUTTON_BASE,
		width: '46px',
		minWidth: '46px',
		padding: 0,
		fontSize: '1.4rem',
	},
	partQtyMaxButton: {
		...ACTION_BUTTON_SMALL,
		minWidth: '64px',
		fontWeight: 800,
	},
};

export default Framework.Components.withRootState(withRouter(MrpStationWork));
